import { ApiCommunications } from '../webmodule-interop.js';
import { DevelopmentError } from '../../other/development-error.js';
import {
  QuoteAttributeDefaultDataProvider,
  QuoteAttributesDataProvider,
  QuoteDefaultsDataProvider,
  QuoteFrameBuyInItem,
  QuoteFrameItemPrice,
  QuoteItemsProvider,
  ResultProcessQuoteFrameItem
} from './quote-interface.js';
import { QuoteItemFrameAttributeTemplateProvider } from '../../../scriptsV6/v6config/v6-quote-item/v6-events.js';
import { V6FrameConfigVersion } from '../../../scripts/v6config/v6config.js';

import type { ApiProvider, NullPromise, ShowErrorEvent, StringDictionary } from '../types/misc.js';
import type { FrameV2, FrameAttributeGroup, FrameBuyInItem } from './frame-data.js';
import type { LitModalScreenBuilder } from './modal-factory-lit.js';
import type { ModalScreenBuilder } from './modal-factory.js';
import type { QCQuoteValues } from './quote-calculations.js';
import type { QuoteContract } from './quote-contract.js';
import type { QuoteShippingInformation } from './shipping-information.js';
import type { TemplateResult } from 'lit';

export interface V6QuoteDefaultsEditor {
  applyQuoteDefaultsToItems: () => Promise<void>;
  resetQuoteDefaults: () => Promise<void>;
  prepareForSave: () => Promise<boolean>;
  ui: HTMLElement;
  dataProvider?: QuoteDefaultsDataProvider;
  releaseResources: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface V6QuoteAttributesEditor {
  refresh: () => Promise<void>;
  applyChanges: () => Promise<void>;
  resetQuoteDefaults: () => Promise<void>;
  resetChanges: () => Promise<void>;
  prepareForSave: () => Promise<boolean>;
  ui: HTMLElement;
  dataProvider?: QuoteDefaultsDataProvider;
  releaseResources: () => Promise<void>;
}

/*
Defines the scope of the template as to where it can be seen and reused
 */
export enum TemplateScope {
  quote = 0,
  user = 1,
  tenant = 2
}

export enum TemplateContent {
  all = 0,
  defaults = 1,
  globals = 2
}

export interface QuoteIGUDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey?: string;

  /**
   * Should this viewer be readonly.. ie quote state, locked viewer etc.
   */
  readonly: boolean;
  /**
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteIGUs: unknown; // V6QuoteIGU[]
}

export interface V6QuoteIGUEditor {
  addIGU: () => Promise<boolean>;
  prepareForSave: () => Promise<boolean>;
  readonly ui: HTMLElement;
  dataProvider?: QuoteIGUDataProvider;
  readonly busy: boolean;
  releaseResources: () => Promise<void>;
}

export interface QuoteFrameProcessingDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey: string;

  /**
   *
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteItem: unknown; // V6QuoteItem
  quoteIGUs: unknown; // V6QuoteIGU[]
  quantity: number;
  quoteDefaults: unknown; // V6AttributeGroup[]

  progress?: (caption: string) => Promise<void>;
}

export const v6FrameUserMode = {
  powerUser: 'POWERUSER',
  standardUser: 'STANDARDUSER'
};

export interface QuoteItemProvider {
  id: string;
  quantity: number;
  /**
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteItem: unknown; // V6QuoteItemExternalStorage
  thumbnailURL: string;
  fetchAsset: (assetType: string) => Promise<string | object | null>;
}

export interface QuoteFrameDataProvider extends QuoteItemProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey: string;
  /**
   * Should this viewer be readonly.. ie quote state, locked viewer etc.
   */
  readonly: boolean;
  quoteIGUs: unknown; // V6QuoteIGU[]
  propertiesTemplate: () => TemplateResult;
  quoteDefaults: unknown; // V6AttributeGroup[]
  changeNotifier?: () => void;
  framePicker?: V6PickerFamilyButton;
  preSelectionFrameReference?: { reference: string; description: string };
  customData?: () => { [key: string]: any };
  quoteAttributesTemplateProvider?: QuoteItemFrameAttributeTemplateProvider;

  /*
                        if this is provided it superceeds quoteDefaults and enables functionality that can update the defaults
                         */
  quoteAttributeDefaultDataProvider?: QuoteAttributeDefaultDataProvider;
}

export interface QuoteFreehandDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  quoteItemCommonId: string | null;
  supplierReferenceOverrideKey: string;
  quantity: number;
  singleUnitGrossSellPrice: number;
  itemSubType: number | null;
}

export interface V6QuoteFrameEditorData {
  price: QuoteFrameItemPrice;
  buyInItems: QuoteFrameBuyInItem[];
  quoteItem: unknown;
  //This is the mode required to edit this frame
  //either power or standard, based on what is edited during the session.
  requiredUserMode: string;
  thumbnail?: string;
  vrml?: string;
  bomView?: { [key: string]: any } | null;
  /**
   * Piece-level BOM from the configure-quote macro. Distinct from `bomView`
   * (human-readable, used in reports). Persisted as a standalone `bom.json`
   * asset by the save pipeline and read directly by the fabrication optimizer.
   */
  bom?: { [key: string]: any } | null;
}
export interface ModifiedAttribute {
  code: string;
  newValue: string | null;
  oldValue: string;
  isPowerUser: boolean;
}

export interface V6QuoteFrameEditor {
  readonly ui: HTMLElement;
  readonly busy: boolean;
  readonly updateComplete: Promise<void> | undefined | null;
  thumbnail: string;
  frameDescription: string;
  releaseResources: () => Promise<void>;
  commit: () => NullPromise<V6QuoteFrameEditorData>;
  resetEditorState: () => void;
  applyTopLevelValue: (code: string, value: string) => void;
  requestUpdate: () => void;
  modifiedAttributes: ModifiedAttribute[];
  changed: boolean;
  /**
   * Swaps the V6QuoteItem data shown by the editor and refreshes the view.
   * Accepts the outer V6QuoteItemExternalStorage wrapper — the inner quoteItem
   * is extracted internally. The frame thumbnail is preserved across the swap.
   */
  reloadFromDataProvider: (quoteItemExternalStorage: unknown) => void;
}

export interface V6VrmlViewer {
  readonly ui: HTMLElement;
  releaseResources: () => Promise<void>;
}

export interface V6QuoteFrameBomViewer {
  readonly ui: HTMLElement;
  hasData: boolean;
  releaseResources: () => Promise<void>;
}

export interface V6PickerFamilyButton {
  caption: string;
  family: string;
  brandingPublicUrl: string;
  brandingCaption: string;
}

export interface V6CustomItemRequestType {
  name: string;
  note: string;
  objectReference: string;
}

// Mirrors the NSwag-generated FinishColorOption/ExtrusionLengthOption in
// scripts/api/dealer-api-interface-all.ts. Re-declared here so webmodule-common does not depend on
// the dealer-specific generated file - keep these in sync if the server contract changes.
export interface FinishColorOption {
  reference: string;
  code: string;
  description: string;
  rollLength: number;
  rollLengthDisplay: string;
}

export interface ExtrusionLengthOption {
  length: number;
  display: string;
  category: string;
}

export interface V6QuoteItemEditorServices {
  getQuoteDefaultsEditorService: (dataProvider: QuoteDefaultsDataProvider) => V6QuoteDefaultsEditor;
  getQuoteAttributesEditorService: (dataProvider: QuoteAttributesDataProvider) => V6QuoteAttributesEditor;
  getQuoteIGUEditorService: (dataProvider: QuoteIGUDataProvider) => V6QuoteIGUEditor;
  getQuoteFrameEditorService: (dataProvider: QuoteFrameDataProvider) => NullPromise<V6QuoteFrameEditor>;
  getQuoteFrameOptionsService: (dataProvider: QuoteFrameDataProvider) => NullPromise<V6QuoteFrameEditor>;
  getQuoteItemFreehandPrice: (dataProvider: QuoteFreehandDataProvider) => NullPromise<QuoteFrameItemPrice>;
  getFramePickerButtons: (supplierId: string) => NullPromise<V6PickerFamilyButton[]>;
  getBomViewer: (dataProvider: QuoteItemProvider) => NullPromise<V6QuoteFrameBomViewer>;
  runVrmlViewer: (dataProvider: QuoteItemsProvider) => Promise<void>;
}

export type EventAsync<TResult> = (() => Promise<TResult>) | Promise<TResult>;
export type EventAsyncExecutor<TResult> = (event: EventAsync<TResult>) => NullPromise<TResult>;

export interface V6ConfigEventHandlers {
  setLangHandler: (
    aEvent: (template: TemplateStringsArray, ...args: unknown[]) => string,
    langEvent: (text: string, namedResource?: string, doNotAddMissing?: boolean) => string
  ) => void;
  setErrorDialogEventHandler: (handler: ShowErrorEvent) => void;
  setApiInjector: (apiInjector: ApiProvider) => void;
  setModalScreenFactory: (aEvent: ModalScreenBuilder, aEvent1: LitModalScreenBuilder) => void;
  setUILockAndExecuteEvent: (event: EventAsyncExecutor<unknown>) => void;
  setUserMode: (userMode: string) => void;
  setApiAdditionalData: (data?: StringDictionary) => void;
}

export interface V6FrameSize {
  width?: number;
  displayWidth?: string;
  height?: number;
  displayHeight?: string;
  displayHeightFirst: boolean;
}

export interface V6QuoteItemSupplierInfo {
  supplierId: string;
  quoteItemVersion: string;
  isVersionCurrent: boolean;
}

export interface V6QuoteBrief {
  quoteCommonId: string;
  fullQuoteNumber: string;
  shippingLeadTime: number;
  deliveryDate: string;
  closedDate: string;
  status: string;
  notesFromSupplier: string;
}

// Interface for quote attributes configuration with triggers
export interface V6QuoteAttributesConfig {
  triggers: V6AttributeTrigger[];
}

export interface V6AttributeTrigger {
  /**
   * The attribute code that acts as a trigger
   */
  code: string;

  /**
   * Whether this attribute should trigger a server-side item calculation
   */
  runssi: boolean;

  /**
   * Whether this attribute should be excluded from validation
   */
  excludeFromValidation: boolean;
}

export interface V6DataUtilities {
  getFrameDimensions: (quoteItem: object /* V6QuoteItem */) => V6FrameSize;
  getCustomFrameDimensions: (quoteItem: object /* V6QuoteItem */) => string | undefined;
  getFrameData: (quoteItem: object /* V6QuoteItem */) => FrameV2;
  getQuoteDefaults: (quoteDefaults?: object /* V6AttributeGroup[] */) => FrameAttributeGroup[];
  getQuoteItemSupplier: (quoteItem: object /* V6QuoteItem */) => V6QuoteItemSupplierInfo;
  getV6SupplierQuoteInfo: (supplierId: string, quoteCommonId: string) => NullPromise<V6QuoteBrief>;
  getFrameBuyInItems: (quoteItem: object /* V6QuoteItem */) => FrameBuyInItem[];
  restartV6Service: (supplierId: string) => Promise<void>;
  getSupplierQuoteDefaults: (supplierId: string) => Promise<object>;
  getCustomItemRequestTypes: (supplierId: string) => Promise<V6CustomItemRequestType[]>;
  getFinishesByResource: (
    supplierId: string,
    resourceType: 'extrusion' | 'component' | 'gasket',
    resourceRef: string
  ) => Promise<FinishColorOption[]>;
  getExtrusionLengths: (
    supplierId: string,
    extrusionRef: string,
    finishColorRef: string
  ) => Promise<ExtrusionLengthOption[]>;
  /*
   * Refresh the quote defaults for the supplier
   * @param supplierId The supplier id
   * @param quoteDefaults The quote defaults to refresh (V6AttributeGroup[])
   * @param quoteIGUs The quote IGUs to refresh (V6QuoteIGU[])
   * @returns The refreshed quote defaults (V6AttributeGroup[])
   */
  refreshQuoteDefaults: (supplierId: string, quoteDefaults: object, quoteIGUs: object) => NullPromise<object>;

  isQuoteIguReferenceValid: (quoteItem: object, quoteIGUS: object) => boolean;
  // Compare two quote items and return both the structured report and markdown
  getQuoteItemComparison: (
    header: string,
    beforeQuoteItem: object | null,
    afterQuoteItem: object | null,
    options?: CompareOptions
  ) => {
    report: CompareReport | null;
    markdown: string;
  } | null;
}

export enum V6OfflineCause {
  Connection = 0,
  Upgrading = 1,
  VersionMismatch = 2,
  InvalidV6ClientCode = 3
}

export interface SupplierInfo {
  id: string;
  description: string;
  online: boolean;
  offlineCause?: V6OfflineCause;
  offlineMessage?: string;
  version: string;
  isMetric: boolean;
  instanceId: string;
  configVersion: string;
  options: StringDictionary | null;
  customOptions: Record<string, any> | null;
}

export interface RequestGetSSI {
  quote: QuoteContract;
  shippingInformation: QuoteShippingInformation;
  supplierReferenceOverrideKey: string;
  additionalData: StringDictionary | null;
}

export interface V6QuoteItemPrice {
  qty: number;
  quoteItemCommonId: string;
  costPrice: number;
  costTax: number;
  overHead: number;
  markup: number;
  grossSellPrice: number;
  itemDisc: number;
  salesCommission: number;
  priceAdj: number;
  netSellPrice: number;
  tax: number;
  costPriceLab: number;
  costTaxLab: number;
  overHeadLab: number;
  markupLab: number;
  grossSellPriceLab: number;
  itemDiscLab: number;
  salesCommissLab: number;
  priceAdjLab: number;
  netSellPriceLab: number;
  taxLab: number;
  grossSellPriceLockAdj: number;
  grossSellPriceLockAdjLab: number;
  unRecoverableWaste: number;
  totalSellingPriceAdjusted: number;
  totalSellPrice: number;
}

export interface V6SSIPriceBreakdown {
  name: string;
  unitPrice: number;
  subType: number;
  sourceData: V6QuoteItemPrice | null;
}

export interface V6ServerSideSpecialItem {
  name: string;
  title: string;
  description: string;
  quantity: number;
  comment: string | null;
  quoteItemContentType: number;
  unitPrice: number;
  unitPriceBreakdown: V6SSIPriceBreakdown[] | null;
  sourceData: V6QuoteItemPrice | null;
  notes: V6SsiSupplierNote[] | null;
}

export const supplierNoteType = {
  clientWarranty: 'client-warranty',
  dealerNote: 'dealer-note',
  clientNote: 'client-note',
  clientDocument: 'client-document',
  dealerDocument: 'dealer-document',
  dealerWarranty: 'dealer-warranty'
} as const;

export interface V6SsiSupplierNote {
  noteType:
    | 'client-warranty'
    | 'dealer-note'
    | 'client-note'
    | 'client-document'
    | 'dealer-document'
    | 'dealer-warranty';
  quoteItemId: string | null;
  content: string;
  name: string;
}

export interface V6QuoteAnnotation {
  severity: 'information' | 'warning' | 'critical';
  content: string;
  relatedQuoteItemIds: string[] | null; // quote item ids
}

export const v6SupplierMarginMarkupValueTypes = {
  margin: 'margin',
  markup: 'narkup'
} as const;

export interface V6SupplierMarginMarkupOverride {
  value: number;
  valueType: 'markup' | 'margin';
}

export interface ResultGetSSI {
  items: V6ServerSideSpecialItem[];
  quoteCalculations: QCQuoteValues | null;
  marginMarkupOverride: V6SupplierMarginMarkupOverride | null;
  notes: V6SsiSupplierNote[] | null;
  annotations: V6QuoteAnnotation[] | null;
}

export interface V6Configurator {
  setSupplierReferenceOverrideKey: (supplierReferenceOverrideKey: string) => void;
  suppliers: (isVersionCritical?: boolean) => Promise<SupplierInfo[] | null>;
  isSupplierOnline: (supplierId: string) => Promise<boolean>;
  performSupplierHealthCheckLazy: () => Promise<void>;
  performSupplierHealthCheck: () => Promise<void>;
  getSupplier: (supplierId: string) => SupplierInfo;
  /**
   *
   * @param dataProvider
   * @param updatePrice if left undefined value is true
   * @returns
   */
  processV6QuoteItem: (
    dataProvider: QuoteFrameProcessingDataProvider,
    updatePrice?: boolean
  ) => Promise<ResultProcessQuoteFrameItem | null>;
  getQuoteSSI: (input: RequestGetSSI) => Promise<ResultGetSSI>;
}

export interface V6ConfigEntryPoint {
  events: V6ConfigEventHandlers;
  config: V6Configurator;
  util: V6DataUtilities;
  editors: V6QuoteItemEditorServices;
}

// Comparison interfaces used by the V6 quote comparator
export interface CompareOptions {
  excludePrice?: boolean; // if true, skip comparing price objects
  excludeQuoteLevelOptions?: boolean; // if true, skip comparing defaultQuoteOptions
}

// Generic before/after value pair
export interface BeforeAfter<T = string | null> {
  before: T | null;
  after: T | null;
}

// Attribute level diff
export interface AttributeDiff {
  code: string;
  label?: string | null;
  value: BeforeAfter<string>;
  displayValue: BeforeAfter<string | null>;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
}

// Per-quote-item differences
export interface QuoteItemDiff {
  quoteItemCommonId: string | null;
  title?: BeforeAfter<string | null>;
  description?: BeforeAfter<string | null>;
  comments?: BeforeAfter<string | null>;
  productLine?: BeforeAfter<string | null>;
  frameDataDescription?: BeforeAfter<string | null>;
  // grouped attribute diffs
  quoteOptions?: AttributeDiff[]; // attributes coming from quoteItem.quoteItemOptions
  frameOptions?: AttributeDiff[]; // attributes coming from the root frame
  nestedFrames?: {
    objectReference: string | null;
    boundaryName?: string | null;
    description?: string | null;
    attributes: AttributeDiff[];
    nested?: any[]; // recursive (kept loose to avoid very verbose typing here)
  }[];
  annotationIssues?: BeforeAfter<any>;
  validationIssues?: BeforeAfter<any>;
  price?: BeforeAfter<any> | null; // only when not excluded
}

export interface QuoteLevelDiff {
  defaultQuoteOptions?: AttributeDiff[]; // flattened attributes
  defaultQuoteOptionsMeta?: BeforeAfter<any> | null; // placeholder if needed later
}

export interface CompareReport {
  quoteId?: BeforeAfter<string | null>;
  title?: BeforeAfter<string | null>;
  quoteLevel?: QuoteLevelDiff | null;
  items: QuoteItemDiff[];
}
export function versionToInteger(ver: string): number {
  // Remove non-digit characters from start and end
  let cleaned = ver.trim();
  cleaned = cleaned.replace(/^[^\d]+/, ''); // Remove leading non-digits
  cleaned = cleaned.replace(/[^\d]+$/, ''); // Remove trailing non-digits

  // Remove any trailing non-digit after the last digit in the version
  cleaned = cleaned.replace(/([\d.]+)[^\d]*$/, '$1');
  let pieces = cleaned.split('.');
  // Enforce exactly 3 segments (2 dots)
  if (pieces.length < 3) {
    // Pad with '0' to reach 3 segments
    while (pieces.length < 3) pieces.push('0');
  } else if (pieces.length > 3) {
    // If more than 3 segments, ignore extras
    pieces = pieces.slice(0, 3);
  }
  pieces[0] = pieces[0].padEnd(3, '0');
  pieces[1] = pieces[1].padEnd(4, '0');
  pieces[2] = pieces[2].padStart(5, '0');
  return parseInt(pieces.join(''));
}
export enum V6PickerDisplayType {
  description = 0,
  code = 1,
  codeDescription = 2
}
export interface V6SupplierOptions {
  isMetric: boolean;
  online: boolean;
  version: string;
  configVersion: string;
  instanceId: string;
  includeShipping: boolean;
  useQuoteGlass: boolean;
  buyIn: boolean;
  widthBeforeHeight: boolean;
  useServerSideItems: boolean;
  isPurchaseOrderIssuedImmediately: boolean;
  isPurchaseOrderNumberMandatory: boolean;
  allowPickupFromSupplier: boolean;
  pickupFromSupplierAddress: string;

  pickerIguDisplayType: V6PickerDisplayType;
  pickerIguQuoteDisplayType: V6PickerDisplayType;
  pickerFrameDisplayType: V6PickerDisplayType;
  pickerAttributeDisplayType: V6PickerDisplayType;
  pickerHideIguThumbnails: boolean;
  pickerHideIguQuoteThumbnails: boolean;
  pickerHideFrameThumbnails: boolean;
  pickerHideAttributeThumbnails: boolean;
  customOptions: Record<string, any>;
}
export const v6VersionMap = {
  hasBeforeAfterApplyQuoteItemEvents: versionToInteger('v1.0.17'),
  hasQuoteOwnerLevelSettings: versionToInteger('v1.0.52'),
  hasInstanceIDInSettings: versionToInteger('v1.0.54'),
  hasNewFinishPicker: versionToInteger('v1.0.55'),
  hasMultipleFrameAdd: versionToInteger('v1.0.55'),
  hasCheckSupplierQuote: versionToInteger('v1.0.58'),
  hasBomView: versionToInteger('v1.0.59'),
  hasQuoteDefaultConversion: versionToInteger('v2.0.3'),
  hasRestartV6: versionToInteger('v2.0.13'),
  hasGlobalAttributesEditor: versionToInteger('v2.0.16'),
  hasAdditionalInfo: versionToInteger('v2.0.23'),
  hasCustomOptions: versionToInteger('v2.0.27'),
  hasLocalization: versionToInteger('v2.0.28'),
  hasCustomItemRequestTypes: versionToInteger('v2.1.0'),
  hasVrml: versionToInteger('v2.1.15'),
  optimizedBlobStorage: versionToInteger('v2.1.24'),
  hasIguHash: versionToInteger('v2.1.26'),
  hasReloadFromDataProvider: versionToInteger('v2.2.4'),
  hasFabricationResourceFinishes: versionToInteger('v3.0.4')
};
export function v6SupportsVersion(version: number | string): boolean {
  const ver = typeof version === 'string' ? versionToInteger(version) : version;
  return globalV6Version >= ver;
}
export function vrmlSupport(): boolean {
  return v6SupportsVersion(v6VersionMap.hasVrml);
}
export function hasOptimizedBlobStorage(): boolean {
  return v6SupportsVersion(v6VersionMap.optimizedBlobStorage);
}
export function reloadFromDataProviderSupport(): boolean {
  return v6SupportsVersion(v6VersionMap.hasReloadFromDataProvider);
}
export interface IV6ConfigManager {
  ver: (version: string) => V6ConfigEntryPoint | undefined;
  lastLoaded?: V6ConfigEntryPoint;
  activeVersion?: V6ConfigEntryPoint;
  dispose?: () => void;
}
export function v6ConfigManager(): IV6ConfigManager | undefined {
  return globalThis.v6ConfigManager as IV6ConfigManager;
}
export function v6ConfigSupplierOptions(supplierId: string): V6SupplierOptions {
  const s = v6Config().getSupplier(supplierId);

  function str2bool(value: string, defaultValue = false): boolean {
    const strvalue = s.options?.[value];
    if (strvalue === undefined) return defaultValue;
    return strvalue === 'true';
  }

  function str2Number(value: string, defaultValue = 0): number {
    const strvalue = s.options?.[value];
    if (strvalue === undefined) return defaultValue;
    const v = parseInt(strvalue);
    if (isNaN(v)) return defaultValue;
    else return v;
  }

  const hasInstanceId = v6SupportsVersion(v6VersionMap.hasInstanceIDInSettings);
  return {
    configVersion: hasInstanceId ? (s.configVersion ?? '') : '',
    instanceId: hasInstanceId ? (s.instanceId ?? '') : '',
    buyIn: str2bool('buyIn'),
    includeShipping: str2bool('includeShipping', true),
    isMetric: s.isMetric,
    online: s.online,
    useQuoteGlass: str2bool('useQuoteGlass'),
    allowPickupFromSupplier: str2bool('allowPickupFromSupplier'),
    pickupFromSupplierAddress: s.options?.['pickupFromSupplierAddress'] ?? '',
    useServerSideItems: str2bool('useServerSideItems'),
    version: s.version,
    widthBeforeHeight: str2bool('widthBeforeHeight', true),
    isPurchaseOrderIssuedImmediately: str2bool('isPurchaseOrderIssuedImmediately', false),
    isPurchaseOrderNumberMandatory: str2bool('isPurchaseOrderNumberMandatory', false),
    pickerFrameDisplayType: str2Number('pickerFrameDisplayType', V6PickerDisplayType.description),
    pickerHideFrameThumbnails: str2bool('pickerHideFrameThumbnails', false),
    pickerAttributeDisplayType: str2Number('pickerAttributeDisplayType', V6PickerDisplayType.description),
    pickerHideAttributeThumbnails: str2bool('pickerHideAttributeThumbnails', false),
    pickerIguDisplayType: str2Number('pickerIguDisplayType', V6PickerDisplayType.description),
    pickerIguQuoteDisplayType: str2Number('pickerIguQuoteDisplayType', V6PickerDisplayType.description),
    pickerHideIguQuoteThumbnails: str2bool('pickerHideIguQuoteThumbnails', false),
    pickerHideIguThumbnails: str2bool('pickerHideIguThumbnails', false),
    customOptions: s.customOptions ?? {}
  };
}
export function isValidV6ConfigVersion(): boolean {
  const manager = v6ConfigManager();
  if (!manager) return false;

  if (!manager.activeVersion) return false;
  return true;
}
export function validateConfigVerison(version?: string) {
  const manager = v6ConfigManager();
  if (!manager) throw new DevelopmentError('V6Config Bootloader has not been run');
  if (!version) {
    if (!manager.activeVersion)
      throw new DevelopmentError('V6Config Bootloader does not have an active version loaded');
  } else if (!manager.ver(version))
    throw new DevelopmentError(
      `V6Config Bootloader does not have an active version ${version} loaded. call v6BootLoader`
    );
}

export function setGlobalV6Version(version: number, versionStr: string) {
  globalV6Version = version;
  globalV6VersionStr = versionStr;
}
export let globalV6Version = 0;
export let globalV6VersionStr = '0.0.0';
export function v6ConfigActiveVersion(): number {
  return globalV6Version;
}
export function v6ConfigActiveVersionStr(): string {
  return globalV6VersionStr;
}
export function v6ConfigEntryPoint(version?: string): V6ConfigEntryPoint {
  validateConfigVerison(version);
  if (!version)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return v6ConfigManager()!.activeVersion!;
  return v6ConfigManager()!.ver(version)!;
}
export function v6Config(version?: string): V6Configurator {
  return v6ConfigEntryPoint(version).config;
}
export function v6Util(version?: string): V6DataUtilities {
  return v6ConfigEntryPoint(version).util;
}
export function v6Editors(version?: string): V6QuoteItemEditorServices {
  return v6ConfigEntryPoint(version).editors;
}
export function v6Events(version?: string): V6ConfigEventHandlers {
  return v6ConfigEntryPoint(version).events;
}
export interface V6MacroVersion {
  version: string;
  configVersion?: string;
  instanceId: string;
  supplierId: string;
  online: boolean;
  offlineCause?: V6OfflineCause;
  offlineMessage?: string;
}
export async function getDealerMacroVersion(comms: ApiCommunications): Promise<V6MacroVersion | undefined> {
  //this is configured as an anonymous call so we dont need a login to get this data.
  const r = await comms.post<unknown>(`api/v6/configitem/suppliers`, {});
  if (!r) return undefined;
  const res: V6MacroVersion = {
    instanceId: (r as any)?.items[0].settings.instanceId ?? '',
    version: (r as any)?.items[0].settings.version ?? '',
    configVersion: (r as any)?.items[0].settings.configVersion ?? '',
    supplierId: (r as any)?.items[0].id,
    online: (r as any)?.items[0].online,
    offlineCause: (r as any)?.items[0].offlineCause,
    offlineMessage: (r as any)?.items[0].offlineMessage
  };
  //if (!res.online && res.version === '') return undefined;
  return res;
}
export function activeV6FrameConfigVersion() {
  let rKey: string | undefined = undefined;
  if (!v6ConfigManager()) return undefined;
  const active = v6ConfigManager()?.activeVersion;
  (v6ConfigManager() as any).versions.forEach((value, key) => {
    if (value === active) rKey = key;
  });
  return rKey;
}
export function activeV6FrameConfigVer(): V6FrameConfigVersion {
  const v = activeV6FrameConfigVersion() ?? 'v0.0.0';
  const values = v.substring(1).split('.');
  return {
    api: parseInt(values[0]),
    major: parseInt(values[1]),
    minor: parseInt(values[2])
  };
}

export type V6BootLoaderEvent = () => Promise<void>;
let _events: V6BootLoaderEvent[] = [];

export function addV6BootLoaderEvent(aEvent: V6BootLoaderEvent) {
  _events.push(aEvent);
}

export function remV6BootLoaderEvent(aEvent: V6BootLoaderEvent) {
  _events = _events.filter(x => x !== aEvent);
}
export async function runBootLoaderEvents() {
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < _events.length; i++) await _events[i]();
}
